/* eslint-disable arrow-body-style */
import { Box, Divider, Grid2, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import InputField from '../../../components/InputField';

export default function AttendeeDetails({ details, values, oneTicketOnly = false }) {
  const { t } = useTranslation('checkout');
  const params = useParams();
  const eventAttributes = useMemo(() => (details?.event?.attributes || []), [details?.event?.attributes]);

  const buildAttributeFields = (productId, attendeeIndex) => {
    const fields = [];
    let lastSection = null;
    eventAttributes.forEach((attribute) => {
      const section = attribute?.section || null;
      if (section && section !== lastSection) {
        fields.push({ type: 'headline', name: section });
        lastSection = section;
      }

      const field = {
        name: `productAttendee_${productId}.${attendeeIndex}.attribute.${attribute.code}`,
        label: attribute?.name || attribute?.code,
        gridSize: { xs: 12 },
        required: attribute?.settings?.isRequired,
      };

      if (attribute?.type === 'select' || attribute?.type === 'autocomplete') {
        field.type = attribute?.type;
        field.multiple = !!attribute?.settings?.multiple;
        field.options = (attribute?.options || []).map((option) => ({
          name: option.label,
          value: option.value,
        }));
      }

      fields.push(field);
    });
    return fields;
  };
  const attendeesByProducts = useMemo(() => {
    let ticketSequence = 0;
    return Object.entries(values)
      .filter(([key, value]) => key.startsWith('product_') && value > 0)
      .reduce((acc, [key, value]) => {
        const productId = parseInt(key.replace('product_', ''));
        const attendees = [];
        const product = details.products.find(({ id }) => id === productId);
        for (let i = 0; i < value; i++) {
          ticketSequence++;
          attendees.push({ internalId: `productAttendee_${productId}_${i}`, sequenceId: ticketSequence, index: i });
        }
        acc.push({ product, attendees });
        return acc;
      }, []);
  }, [values, details?.products]);

  const getFields = (product, id) => {
    if (details?.event?.frontendSettings?.attendeeCustomFieldDefinition) {
      return details?.event?.frontendSettings?.attendeeCustomFieldDefinition
        .map((fieldDef) => ({
          ...fieldDef,
          name: `productAttendee_${product.id}.${id}.${fieldDef.name}`,
        }));
    }
    const fields = [
      {
        name: `productAttendee_${product.id}.${id}.forename`,
        label: 'Vorname',
        required: true,
        gridSize: { xs: 12, md: 6 },
      },
      {
        name: `productAttendee_${product.id}.${id}.name`,
        label: 'Nachname',
        required: true,
        gridSize: { xs: 12, md: 6 },
      },
      {
        name: `productAttendee_${product.id}.${id}.company`,
        label: 'Firma',
        gridSize: { xs: 12 },
      },
      {
        name: `productAttendee_${product.id}.${id}.emailAddress`,
        label: 'E-Mail (an diese E-Mail wird die Einlasskarte gesendet)',
        type: 'email',
        required: true,
        gridSize: { xs: 12 },
      },
    ];

    fields.push(...buildAttributeFields(product.id, id));

    if (params.slug === 'ticketverkauf-2' || params.slug === 'natural-beauty-kongress-2025') {
      fields.push(
        { type: 'headline', name: 'Welche Workshops möchtest du besuchen?' },
        {
          name: `productAttendee_${product.id}.${id}.custom_attributes.workshopblock_1`,
          label: 'Workshopblock 1',
          required: true,
          gridSize: { xs: 12 },
          type: 'select',
          options: [
            {
              name: 'Workshop 1',
              value: 'workshop1',
            },
            {
              name: 'Workshop 2',
              value: 'workshop2',
            },
            {
              name: 'Workshop 3',
              value: 'workshop3',
            },
            {
              name: 'Workshop 4',
              value: 'workshop4',
            },
          ],
        },
        {
          name: `productAttendee_${product.id}.${id}.custom_attributes.workshopblock_2`,
          label: 'Workshopblock 2',
          required: true,
          gridSize: { xs: 12 },
          type: 'select',
          options: [
            {
              name: 'Workshop 1',
              value: 'workshop1',
            },
            {
              name: 'Workshop 2',
              value: 'workshop2',
            },
            {
              name: 'Workshop 3',
              value: 'workshop3',
            },
            {
              name: 'Workshop 4',
              value: 'workshop4',
            },
          ],
        },
        { type: 'headline', name: 'Dinner' },
        {
          name: `productAttendee_${product.id}.${id}.custom_attributes.attendDinner`,
          label: 'Möchtest du am Dinner teilnehmen?',
          gridSize: { xs: 12 },
          type: 'select',
          options: [
            { name: 'Ja', value: 'true' },
            { name: 'Nein', value: 'false' },
          ],
        },
        {
          name: `productAttendee_${product.id}.${id}.custom_attributes.intolerances`,
          label: 'Welche Unverträglichkeiten hast du?',
          gridSize: { xs: 12 },
        },
        { type: 'headline', name: 'Wettbewerb' },
        {
          name: `productAttendee_${product.id}.${id}.custom_attributes.attendCompetition`,
          label: 'Möchtest du am Wettbewerb teilnehmen?',
          gridSize: { xs: 12 },
          type: 'select',
          options: [
            { name: 'Ja', value: 'true' },
            { name: 'Nein', value: 'false' },
          ],
        },
      );
    }

    return fields;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ marginBottom: 2 }}>
        {t('steps.enterAttendee')}
      </Typography>
      {attendeesByProducts.map(({ product, attendees }, index) => attendees.map((attendee, attendeeIndex) => (
        <Grid2 container key={attendee.internalId} justifyContent="center" alignItems="center">
          <Grid2 size={12}>
            <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '10px' }}>
              {oneTicketOnly ? product.name : `Ticket #${attendee.sequenceId} - ${product.name}`}
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              {getFields(product, attendee.index).map((field) => (field.type === 'headline'
                ? (
                  <Typography variant="subtitle1" style={{ marginTop: '20px', marginBottom: '10px' }}>
                    {field.name}
                  </Typography>
                )
                : (<InputField key={field.name} {...field} />)))}
            </Grid2>
          </Grid2>

          {!(attendeesByProducts.length === (index + 1) && attendees.length === (attendeeIndex + 1)) && (
            <Grid2 size={12} sx={{ marginTop: '30px' }}>
              <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.5)' }} />
            </Grid2>
          )}
        </Grid2>
      )))}
    </Box>
  );
}
